import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'store/create-store';
import { SiteConfigData, SiteConfigLoadStatus, SiteConfigState } from 'store/siteConfig/types';

export const getSiteConfigState = (state: RootState): SiteConfigState => state.siteConfig;

export const getSiteConfigData = createSelector(
  [getSiteConfigState],
  (state: SiteConfigState): SiteConfigData[] | null => state?.data
);

// A refetch keeps the previous data, so 'loaded' wins over an in-flight or failed refetch.
export const getSiteConfigLoadStatus = (state: RootState): SiteConfigLoadStatus => {
  const { data, error } = state.siteConfig;
  if (data) return 'loaded';
  return error ? 'failed' : 'loading';
};

//Components configuration

const getConfigBySectionAndComponent = createSelector(
  [
    (state: RootState) => state.siteConfig.data,
    (_: RootState, sectionName: string, componentName: string) => ({ sectionName, componentName }),
  ],
  (data: SiteConfigData[] | null, { sectionName, componentName }) =>
    // Read by the header on every page; bad data must not throw.
    Array.isArray(data)
      ? data.find(item => item.section === sectionName && item.component === componentName)?.details
      : undefined
);

export const getSitewideConfig = (state: RootState, componentName: string) =>
  getConfigBySectionAndComponent(state, 'SITEWIDE', componentName);

const isStringRecord = (value: unknown): value is Record<string, string> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every(item => typeof item === 'string');

export const getHubspotFormIds = (state: RootState): Record<string, string> | undefined => {
  const details = getSitewideConfig(state, 'hubspot_forms');
  return isStringRecord(details) ? details : undefined;
};

export const getHeaderBarConfig = createSelector(
  [
    (state: RootState) => getSitewideConfig(state, 'banner'),
    (state: RootState) => getSitewideConfig(state, 'header'),
  ],
  (bannerConfig, headerConfig) => ({ bannerConfig, headerConfig })
);

export const getLandingConfig = (state: RootState, componentName: string) =>
  getConfigBySectionAndComponent(state, 'LANDING', componentName);

export const getLandingTopAndImpactConfig = createSelector(
  [
    (state: RootState) => getLandingConfig(state, 'top_card'),
    (state: RootState) => getLandingConfig(state, 'impact_card'),
    (state: RootState) => getLandingConfig(state, 'give_by'),
    (state: RootState) => getLandingConfig(state, 'recipient_spotlight'),
  ],
  (landingTopCardConfig, landingImpactConfig, giveByConfig, landingRecipientSpotlightConfig) => ({
    landingTopCardConfig,
    landingImpactConfig,
    giveByConfig,
    landingRecipientSpotlightConfig,
  })
);

export const getDonateConfig = (state: RootState, componentName: string) =>
  getConfigBySectionAndComponent(state, 'DONATE', componentName);

export const getProfileConfig = (state: RootState, componentName: string) =>
  getConfigBySectionAndComponent(state, 'PROFILE', componentName);

export const getImpactConfig = (state: RootState, componentName: string) =>
  getConfigBySectionAndComponent(state, 'IMPACT_SCORE', componentName);

export const getAutopilotConfig = (state: RootState, componentName: string) =>
  getConfigBySectionAndComponent(state, 'AUTOPILOT', componentName);

export const getSummaryConfig = (state: RootState, componentName: string) =>
  getConfigBySectionAndComponent(state, 'SUMMARY', componentName);
